import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import { api } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  CreditCard, 
  Check, 
  ArrowRight, 
  Loader2,
  Crown,
  Zap,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

const planIcons = {
  free: Zap,
  starter: Zap,
  pro: Crown,
  team: Crown
};

export default function BillingPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState({});
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [buyingSeats, setBuyingSeats] = useState(false);

  useEffect(() => {
    fetchBilling();
    
    // Check for payment completion
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchBilling = async () => {
    try {
      const [subRes, plansRes] = await Promise.all([
        api.get('/billing/current'),
        api.get('/billing/plans')
      ]);
      setSubscription(subRes.data);
      setPlans(plansRes.data.plans);
    } catch (error) {
      console.error('Failed to fetch billing', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    
    const maxAttempts = 5;
    let attempts = 0;
    
    const poll = async () => {
      try {
        const res = await api.get(`/billing/checkout/status/${sessionId}`);
        
        if (res.data.payment_status === 'paid') {
          toast.success('Payment successful! Your plan has been upgraded.');
          fetchBilling();
          setCheckingPayment(false);
          // Clear URL params
          window.history.replaceState({}, '', '/app/billing');
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setCheckingPayment(false);
          toast.info('Payment status pending. It may take a moment to process.');
        }
      } catch (error) {
        setCheckingPayment(false);
        console.error('Failed to check payment status', error);
      }
    };
    
    poll();
  };

  const handleUpgrade = async (planKey) => {
    try {
      const originUrl = window.location.origin;
      const res = await api.post(`/billing/checkout?plan=${planKey}&origin_url=${encodeURIComponent(originUrl)}`);
      
      if (res.data.checkout_url) {
        window.top.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error('Failed to start checkout');
      console.error(error);
    }
  };

  const handleBuySeats = async () => {
    setBuyingSeats(true);
    try {
      const originUrl = window.location.origin;
      const res = await api.post(`/billing/checkout-seats?origin_url=${encodeURIComponent(originUrl)}`);
      if (res.data.checkout_url) {
        window.top.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start seat checkout');
    } finally {
      setBuyingSeats(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </AppLayout>
    );
  }

  const { subscription: sub, plan_info, usage, limits_reached, seats } = subscription || {};
  const currentPlan = sub?.plan || 'free';

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="billing-container">
        {/* Header */}
        <div>
          <h1 className="page-title">Billing</h1>
          <p className="text-slate-500">Manage your subscription and usage</p>
        </div>

        {checkingPayment && (
          <Card className="card-industrial border-orange-200 bg-orange-50">
            <CardContent className="py-4 flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
              <span className="text-orange-700">Checking payment status...</span>
            </CardContent>
          </Card>
        )}

        {/* Current Plan */}
        <Card className="card-industrial">
          <CardHeader>
            <CardTitle className="section-title text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Current Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                {(() => {
                  const Icon = planIcons[currentPlan] || Zap;
                  return <Icon className="w-8 h-8 text-orange-500" />;
                })()}
                <div>
                  <h3 className="font-bold text-xl text-slate-900 capitalize">{plan_info?.name || currentPlan}</h3>
                  <p className="text-slate-500">
                    {currentPlan === 'free' ? 'Free Plan' : currentPlan === 'single' ? '£9 one-time' : `£${plan_info?.price}/month`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {sub?.paid_credits > 0 && currentPlan !== 'pro' && (
                  <Badge className="bg-orange-100 text-orange-700 border-orange-200" data-testid="paid-credits-badge">
                    {sub.paid_credits} paid {sub.paid_credits === 1 ? 'check' : 'checks'}
                  </Badge>
                )}
                <Badge 
                  variant={sub?.status === 'active' ? 'default' : 'secondary'} 
                  className={`capitalize ${sub?.status === 'canceled' ? 'bg-red-100 text-red-700 border-red-200' : ''} ${sub?.status === 'past_due' ? 'bg-amber-100 text-amber-700 border-amber-200' : ''} ${sub?.status === 'canceling' ? 'bg-amber-100 text-amber-700 border-amber-200' : ''}`}
                  data-testid="subscription-status-badge"
                >
                  {sub?.status || 'active'}
                </Badge>
              </div>
            </div>

            {/* Usage */}
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <div className="p-4 bg-slate-50 rounded-sm">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-600">Bids Used</span>
                  <span className="font-mono font-semibold">
                    {usage?.bids} / {usage?.max_bids === -1 ? '∞' : usage?.max_bids}
                  </span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all ${limits_reached?.bids ? 'bg-red-500' : 'bg-orange-500'}`}
                    style={{ 
                      width: usage?.max_bids === -1 
                        ? '10%' 
                        : `${Math.min(100, (usage?.bids / usage?.max_bids) * 100)}%` 
                    }}
                  />
                </div>
              </div>
              <div className="p-4 bg-slate-50 rounded-sm">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-600">Team Seats</span>
                  <span className="font-mono font-semibold" data-testid="seats-count">
                    {seats?.used ?? usage?.members ?? 0} / {seats?.limit ?? usage?.max_members ?? 3}
                  </span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all ${limits_reached?.members ? 'bg-red-500' : 'bg-orange-500'}`}
                    style={{ width: `${Math.min(100, ((seats?.used ?? 0) / (seats?.limit ?? 3)) * 100)}%` }}
                  />
                </div>
                {seats?.blocks > 0 && (
                  <p className="text-[10px] text-slate-500 mt-1">
                    Base 3 + {seats.blocks} block{seats.blocks > 1 ? 's' : ''} ({seats.blocks * 5} extra)
                  </p>
                )}
              </div>
            </div>

            {/* Seat Upgrade Prompt — Pro users at or near limit */}
            {currentPlan === 'pro' && seats && seats.used >= seats.limit && (
              <div className="mt-4 p-4 border border-amber-200 bg-amber-50/80 rounded-sm flex items-center justify-between gap-4" data-testid="seat-upgrade-prompt">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">Seat limit reached</p>
                    <p className="text-xs text-amber-700">Add 5 more seats for £99/month</p>
                  </div>
                </div>
                <Button
                  onClick={handleBuySeats}
                  disabled={buyingSeats}
                  className="btn-primary flex-shrink-0"
                  data-testid="buy-seats-btn"
                >
                  {buyingSeats ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Seats'}
                </Button>
              </div>
            )}

            {/* Optional: show buy seats button for Pro users not yet at limit */}
            {currentPlan === 'pro' && seats && seats.used < seats.limit && (
              <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                <span>{seats.limit - seats.used} seat{seats.limit - seats.used !== 1 ? 's' : ''} available</span>
                <button
                  onClick={handleBuySeats}
                  disabled={buyingSeats}
                  className="text-orange-600 hover:text-orange-700 underline"
                  data-testid="buy-more-seats-link"
                >
                  Need more seats?
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Available Plans */}
        <div>
          <h2 className="section-title text-lg mb-4">Available Plans</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(plans).filter(([key]) => key !== 'free' || currentPlan === 'free').map(([key, plan]) => {
              const isCurrentPlan = key === currentPlan;
              const Icon = planIcons[key] || Zap;
              const priceLabel = key === 'single' ? '£9' : key === 'pro' ? '£149' : `£${plan.price}`;
              const periodLabel = key === 'single' ? 'one-time' : key === 'pro' ? '/month' : plan.period || '/mo';
              
              return (
                <Card 
                  key={key} 
                  className={`card-industrial ${isCurrentPlan ? 'border-orange-500 bg-orange-50/50' : ''} ${key === 'pro' ? 'ring-1 ring-orange-500/20' : ''}`}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Icon className={`w-5 h-5 ${isCurrentPlan ? 'text-orange-500' : 'text-slate-400'}`} />
                      <h3 className="font-bold text-slate-900">{plan.name}</h3>
                      {isCurrentPlan && (
                        <Badge variant="outline" className="ml-auto">Current</Badge>
                      )}
                    </div>
                    
                    <div className="mb-4">
                      <span className="text-3xl font-mono font-bold">{priceLabel}</span>
                      <span className="text-slate-500 text-sm ml-1">{periodLabel}</span>
                    </div>
                    
                    <ul className="space-y-2 mb-6 text-sm">
                      {plan.features?.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {!isCurrentPlan && key !== 'free' && (
                      <Button
                        onClick={() => handleUpgrade(key)}
                        className={key === 'pro' ? 'w-full btn-primary' : 'w-full btn-secondary'}
                        data-testid={`upgrade-${key}-btn`}
                      >
                        {key === 'single' ? 'Buy Bid Check — £9' : currentPlan === 'free' ? 'Subscribe — £149/mo' : 'Upgrade'}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
